// Need to use the React-specific entry point to import createApi
import { endOfDay, startOfDay } from 'date-fns'

import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'

import { prepareDefaultHeaders } from '.'
import { SubgenreMap } from '../features/account'
import { UserLanguage } from '../features/account/types/User'
import { ActiveUsersData } from '../features/active-users/types/ActiveUsersData'
import { Analysis, UpdateAnalysisAnswerPayload } from '../features/analysis/types/Analysis'
import { AnalysisNote, AnalysisNotes } from '../features/analysis/types/AnalysisNotes'
import { SearchArtistQueryParams } from '../features/artist-search/types/SearchArtistQueryParams'
import { Bookmark } from '../features/bookmarks/types/Bookmark'
import { BookmarksData } from '../features/bookmarks/types/BookmarksData'
import { Concept } from '../features/concepts/types/Concept'
import { EstimatePlatformType } from '../features/estimates/types/EstimatePlatformType'
import { EstimatesList } from '../features/estimates/types/EstimatesList'
import featureService from '../features/feature/services/FeatureService'
import { TopFeature } from '../features/feature/types/top-feature'
import { FeatureChoice } from '../features/game-features/types/types'
import { SearchGamesQueryParams } from '../features/game-search/types/SearchGamesQueryParams'
import { Game, GenreInnovatorGame, NewGameMetaData } from '../features/game/types/Game'
import { GameAnalysisSuggestion } from '../features/game/types/GameAnalysisSuggestion'
import type { GameAndAnalysis } from '../features/game/types/GameAndAnalysis'
import { GameReview } from '../features/game/types/GameReview'
import { ConventionalCategory, ConventionalGenre, ConventionalSubgenre } from '../features/genre-taxonomy'
import type { FiltersForTrackedGamesResponse } from '../features/live-events/types/FiltersForTrackedGamesResponse'
import {
  LiveEventCollection,
  LiveEventCollectionDB,
  LiveEventEvolutionEntry,
  LiveEventEvolutionEntryDB,
  LiveEventEvolutionEntryStart,
  LiveEventEvolutionTypeEnum,
} from '../features/live-events/types/LiveEventCollection'
import type { TrackedGame } from '../features/live-events/types/TrackedGame'
import type {
  LiveEventReview,
  LiveTrackingEvent,
  TrackedGamePerformance,
  TrackedGamePerformanceRequest,
  TrackedGamePerformanceResponse,
  TrackingEventByGame,
  TrackingEventsByGame,
} from '../features/live-events/types/TrackingEvents'
import { MetricWithComparisons } from '../features/market-analytics/types/MarketLevelBlueprintListItem'
import { DataFilters } from '../features/market-explorer/types/MarketExplorerDataFilters'
import type {
  MarketExplorerSegmentData,
  MarketExplorerSegmentDataFeature,
  MotivationStatsWithGameScore,
} from '../features/market-explorer/types/MarketExplorerSegmentData'
import type { Market } from '../features/markets'
import { ProfileMotivation } from '../features/motivations/types/ProfileMotivation'
import type { Store } from '../features/store'
import type { UpdateImpact } from '../features/update-impacts'
import { uploadBatch } from '../helpers/upload'
import { UploadedFile } from '../internal/types/UploadedFile'
import languageService from '../services/LanguageService'
import { Analyst } from '../types/Analyst'
import { AnalystComment } from '../types/AnalystComment'
import { FeaturesEffect } from '../types/FeatureEffects'
import { GameFilter } from '../types/GameFilter'
import { LanguageMap } from '../types/LanguageMap'
import { MongoDBObject } from '../types/MongoDBObject'
import { Setting } from '../types/Setting'
import { TranslationMap } from '../types/TranslationMap'
import { VersionInfo } from '../types/VersionInfo'
import { BlueprintFamilyExampleImage } from './blueprintFamilies/exampleImages'
import { sanitizeGameIds } from './utils/sanitizeGameIds'

// One event type a blueprint family belongs to, with the distinct cohort games that ran
// the family under that type in the period's last month. See `BlueprintFamilyApiItem.eventTypes`.
export type BlueprintFamilyEventTypeMembership = {
  typeId: string
  categoryId: string
  distinctGamesCount: number
}

// One game that FIRST ran the entity inside the window — an entry of
// `BlueprintFamilyApiItem.newGames` / `BlueprintFamilyRollupApiItem.newGames`.
// `firstEventDate` is that first event's start (epoch ms). A game the backend can't
// resolve comes back with `gameNames: {}` and no icons.
export type BlueprintFamilyNewGame = {
  gameId: string
  gameNames: TranslationMap
  // Same fields as on `BlueprintApiItem` (GR-3245): `gameIcon` for mobile and PC/console,
  // `gameIcons` (sizes, mobile only) in the same order as on the blueprint rows.
  gameIcon?: IconWrapper
  gameIcons?: string[]
  firstEventDate: number
  // `false` when the game is outside the plan of the user's organization (GR-2916). Always
  // sent by the customer endpoints; absent on internal ones, where it reads as allowed.
  gameAllowed?: boolean
}

// Response shape for POST /blueprint-families (list endpoint).
// Mirrors BE BlueprintFamilyRespDto; @JsonInclude(NON_NULL) means any field may be absent.
// Note: `descriptions` and `modifiedAt` are NOT returned on the list — only on the detail endpoint.
export type BlueprintFamilyApiItem = {
  id: string
  names: TranslationMap
  conventionalCategoryIds?: string[]
  conventionalGenreIds?: string[]
  conventionalSubgenreIds?: string[]
  discontinued: boolean
  gamesCount?: number
  liveEventsCount?: number
  // Every event type the family's events carry inside the selected period, ordered by
  // distinct games over the WHOLE period descending and ties by `typeId` ascending, so
  // `eventTypes[0]` is the leading type. Each entry's `distinctGamesCount` is the
  // period's LAST month only, like the family's own `distinctGamesCount`: the list is
  // not necessarily sorted by it, and a type nobody ran last month stays listed with 0.
  // Per-type counts are disjoint by tag, not by game, so they can sum past the family's
  // count, but no single type's count exceeds it.
  //
  // An empty list is reachable: a family with all-time events but none inside the
  // window, or none on the request's platform (mobile only unless `includePcConsole`;
  // the list endpoint is never asked for PC/console). Live Event Analytics drops that
  // family from the catalogue rather than filing it under a guessed type or under its
  // curated types (see `familyEventTypeMemberships`); only the Data Glossary files it by
  // `allowedEventTypeIds` (see `familyGlossaryEventTypeMemberships`).
  //
  // This replaced `mostUsedTypeId` / `mostUsedTypeIdCategory`, which were an argmax
  // over all-time event counts and disagreed with this ordering for 17 of 154
  // families on prod (11 of them flipping category), so `eventTypes[0].typeId` is
  // NOT a drop-in for the old field.
  //
  // This is raw usage: odd-case events put their type in here too. Placement
  // reads it through `familyEventTypeMemberships`, which keeps only the entries
  // in `allowedEventTypeIds` once the family has an allowed list.
  eventTypes?: BlueprintFamilyEventTypeMembership[]
  // Analyst-curated event types of the family, independent of window and usage,
  // so it can list types the family never ran. Empty (or absent) when uncurated,
  // in which case every `eventTypes` entry places the family.
  allowedEventTypeIds?: string[]
  revenue?: MetricWithComparisons
  downloads?: MetricWithComparisons
  dau?: MetricWithComparisons
  arpdau?: MetricWithComparisons
  adoptionRate?: {
    windowed: number
    instantaneous: number
  }
  // Cohort games with an event for this blueprint during the period's LAST month — the
  // adoption numerator, and the numerator behind `adoptionRate.windowed`. Equals the
  // last `adoptionRate` point's `distinctGamesCount` of this family's trends series.
  //
  // The selected period is the last `windowDays / 30` COMPLETE calendar months (UTC),
  // ending on the last day of the month before the one in progress, so it moves only on
  // the 1st. It is not `windowDays` long: 6, 12 and 24 months hold about 181–184,
  // 365–366 and 730–731 days. Every adoption, momentum, density and trends figure uses
  // it; the duration distributions still cover the `windowDays` ending today.
  distinctGamesCount?: number
  // The cohort every numerator on this row is counted over, and the denominator
  // behind `adoptionRate.*`: games whose first recorded live event is on or before the
  // period's first day. Identical on the list, detail, rollup and momentum responses and
  // on every trends point (`trackedGamesCount`) for the same request. A longer window
  // starts earlier, so it holds fewer games. `0` yields rates of `0.0`, never NaN.
  // Optional only for the transition — see `resolveCohortSize`.
  cohortGamesCount?: number
  // Cohort games whose FIRST-EVER event of this blueprint falls inside the whole
  // period (not only its last month), newest first. Always present on the
  // new contract (`[]` when none) and never truncated — bounded by
  // `cohortGamesCount`. Optional only for the transition.
  newGames?: BlueprintFamilyNewGame[]
}

// Response shape for POST /blueprint-families/{id} (detail endpoint).
// Extends the list item shape with BE-only detail fields (descriptions, modifiedAt).
export type BlueprintFamilyDetailApiItem = BlueprintFamilyApiItem & {
  descriptions?: TranslationMap
  modifiedAt: number
}

// Response shape for GET /blueprint-families/glossary.
// Mirrors BE BlueprintFamilyGlossaryRespDto. Editorial-only payload (no analytics fields).
// Discontinued families are never returned; the endpoint takes no parameters at all.
// See `docs/reskins/backend-handoff-market-blueprint-data-glossary.md` for context.
//
// Notes about the live shape (versus the handoff doc):
// - The BE reuses the existing entity-level `descriptions` field (rich HTML, e.g. `<p>...</p>`
//   with character entities). Render with `DOMPurify.sanitize` + `dangerouslySetInnerHTML`,
//   as `MarketLevelBlueprintsTabView` does (see `src/features/data-glossary/components/`).
// - There is NO `category` field. Categories are encoded in the localized name as
//   `"Category: Name"` (e.g. `"PvP: Ranked Season"`, `"Monetization - Bundle/Offer: Limited-time Shop"`).
//   FE splits on the first `:` to derive the chip label.
// - `images` are analyst-curated example screenshots, each picked from the live event it
//   came from and carrying that event's resolved game/event names. Empty array is the
//   steady state until analysts curate images via the admin tooling. Entries the BE can no
//   longer resolve against their event are omitted rather than returned unlinked.
// - The BE normalizes missing translations to empty strings (rather than omitting the key).
//   `localize()` on the FE side falls back to `en` when the requested language is empty.
export type BlueprintFamilyGlossaryItem = {
  id: string
  names: TranslationMap
  descriptions?: TranslationMap
  images?: BlueprintFamilyExampleImage[]
}

// Mirrors BE IconWrapper (backend-common). @JsonInclude(NON_NULL): all fields optional.
export type IconWrapper = {
  id?: string
  url?: string
  mimeType?: string
}

// Response shape for GET /blueprint-families/{id}/blueprints (game-level list).
// Mirrors BE LiveEventBlueprintRespDto; @JsonInclude(NON_NULL) means any non-id field may be absent.
// `earliestEvent` / `latestEvent` are ms-since-epoch (UTC midnight aligned): the earliest start and
// latest end of this game-level blueprint's own events, scheduled ones included — not the game's
// overall span. Both are 0 when none of its events has a run.
// `names` ships per-item as of 2026-05-13 (see handoff doc) — kept optional to match
// the BE's NON_NULL contract, but in practice it will always be populated.
// As of GR-2916 the list endpoint returns items sorted by `srank` ascending with
// unranked (no `srank`) items last — render in the order received.
export type BlueprintApiItem = {
  id: string
  names?: TranslationMap
  // Market-locale → HTML description of the game-level blueprint (e.g. { us: '<p>…</p>' }). GR-2916.
  descriptions?: TranslationMap
  gameId?: string
  gameNames?: TranslationMap
  // Game studio / publisher name. GR-2916.
  artist?: string
  // US grossing rank of the game. Only present when ranked (< 2000); absent = unranked. GR-2916.
  srank?: number
  gameIcon?: IconWrapper
  gameIcons?: string[]
  conventionalSubgenreId?: string
  conventionalGenreId?: string
  liveEventsCount?: number
  earliestEvent?: number
  latestEvent?: number
  // This game-level blueprint is retired: the game no longer runs these events.
  // Independent of `gameDiscontinued` — all four combinations occur on prod.
  discontinued?: boolean
  // The GAME is no longer tracked. When true the uplift keys are absent (they are
  // already optional here). Independent of `discontinued` above.
  //
  // Both flags replace a join against `/games/tracked/list`, which was the only source
  // of them before and had to be fetched up front purely to split the adopters table.
  gameDiscontinued?: boolean
  // `false` when the game is outside the plan of the user's organization (GR-2916). The row
  // and its detail then carry `liveEvents: []`, while `liveEventsCount`, `earliestEvent` and
  // `latestEvent` still describe the real events. Absent (internal endpoints) reads as allowed.
  gameAllowed?: boolean
}

// Evolution types the BE ships on nested game-blueprint live-event entries (GR-2916).
// Wire subset of the internal LiveEventEvolutionTypeEnum values.
export type LiveEventEvolutionType = 'RESKIN' | 'MECHANIC_CHANGE'

// Nested live-event entry on the game-level blueprint detail response.
// Mirrors BE LiveEventBlueprintWithDetailsRespDto.LiveEventRespDto; timestamps are Java primitive `long` (always present, epoch ms).
// `name` / `types` are NON_NULL fields added in GR-2916 — omitted when absent.
export type BlueprintLiveEvent = {
  eventId: string
  startDate: number
  endDate: number
  name?: string
  types?: LiveEventEvolutionType[]
}

// Response shape for GET /blueprint-families/{id}/blueprints/{blueprintId} (game-level detail).
// Mirrors BE LiveEventBlueprintWithDetailsRespDto which extends the list shape. `modifiedAt` is a BE primitive
// `long` (always present) and `liveEventsCount` is always present on detail. It counts distinct events, as on
// the list; `liveEvents` has one entry per event instance, so its length is the instance count.
export type BlueprintDetailApiItem = Omit<BlueprintApiItem, 'liveEventsCount'> & {
  liveEventsCount: number
  blueprintFamilyId?: string
  blueprintFamilyName?: TranslationMap
  conventionalGenreId?: string
  modifiedAt: number
  liveEvents?: BlueprintLiveEvent[]
}

// Allowed values for the `windowDays` query param on the trend/catalogue endpoints.
// BE returns 400 for any other value. 720 (24mo) is supported as of GR-2916.
export type BlueprintFamilyTrendsWindowDays = 30 | 90 | 180 | 360 | 720

// Game-segment scope shared by the market-blueprint POST endpoints.
// `conventionalSubgenreIds` is the ONLY scoping field the backend accepts: unknown body
// fields are rejected with a 400 (unlike unknown query params, which are ignored), so
// adding `conventionalCategoryIds` / `conventionalGenreIds` back here would fail loudly on
// every scoped request. Callers expand a category / genre selection down to its subgenres
// client-side — see `resolveEffectiveSubgenreIds` in `catalogueUtils`.
export interface ConventionalScope {
  conventionalSubgenreIds?: string[]
}

export function buildConventionalScopeBody(scope: ConventionalScope): Record<string, string[]> {
  return { conventionalSubgenreIds: scope.conventionalSubgenreIds ?? [] }
}

// Stable cache-key fragment encoding the segment scope, so distinct subgenre
// selections cache independently in RTK Query.
export function conventionalScopeKey(scope: ConventionalScope): string {
  return [...(scope.conventionalSubgenreIds ?? [])].sort().join(',') || 'all'
}

// A point carries EITHER `date` (daily) or `month` (monthly), never both. Every trends
// endpoint is monthly; the daily shape remains for the chart helpers, which can still
// bucket a daily series into months. Keeping the two apart at the type level is
// deliberate: a monthly point used to key on `date` while holding "2026-09", which made
// a monthly series indistinguishable from a daily one at runtime.

// Single daily time-series point (date is "yyyy-MM-dd").
export type BlueprintFamilyTrendsSeriesPoint = {
  date: string
  value: number
}

// Single monthly time-series point (month is "yyyy-MM").
export type BlueprintFamilyTrendsMonthlyPoint = {
  month: string
  value: number
}

// Either granularity, for code that only needs to plot a series without caring which
// granularity it is. Read the axis key with `trendsPointKey`.
export type BlueprintFamilyTrendsPoint = BlueprintFamilyTrendsSeriesPoint | BlueprintFamilyTrendsMonthlyPoint

// The axis key of a point, whichever granularity it is: "yyyy-MM-dd" or "yyyy-MM".
// Both sort lexicographically, and a "yyyy-MM" prefix of a "yyyy-MM-dd" key is its month.
export function trendsPointKey(point: BlueprintFamilyTrendsPoint): string {
  return 'date' in point ? point.date : point.month
}

// Monthly adoption point. The axis is exactly the period's `windowDays / 30` complete
// months, oldest first; a month with no activity is a `0` point, never left out.
//
// - `distinctGamesCount`: cohort games that ran the entity on at least one day of the
//   month, each counted once. A COUNT, not a rate.
// - `trackedGamesCount`: the request's `cohortGamesCount`, the same on every point.
//
// The month's share is `distinctGamesCount / trackedGamesCount` (see `adoptionShareOf`);
// the response does not carry it. When `trackedGamesCount` is `0`, so is
// `distinctGamesCount`. The last point's count equals the list / detail
// `distinctGamesCount` (family series) or the rollup's `distinctGamesCount` (event-group and
// event-type series), so its share equals the matching `windowed` rate.
export type BlueprintFamilyAdoptionMonthlyPoint = {
  month: string
  distinctGamesCount: number
  trackedGamesCount: number
}

// One item of the batched trends endpoints — `/trends/batch`, `/trends/event-groups` and
// `/trends/event-types`. They share a single BE DTO (BlueprintFamilyTrendsBatchRespDto);
// only what `id` refers to differs, so each module aliases this under its own name rather
// than restating the fields.
//
// `density` is per adopting game: each point is the entity's live-event days in the month
// (events active on each UTC day, overlapping events each counted) divided by the segment
// games with at least one active day of the entity that month × the days in the month,
// i.e. the average number of its events running at once per adopting game per day. A month
// with no adopter is `0`. Its last point is the Density tile's figure.
// `activeLiveEventsCount` is the mean of the day's active events across the whole
// segment, not divided by any games. Both count the segment, not the cohort.
//
// @JsonInclude(NON_NULL): `density` is absent when the segment holds no game, and
// `activeLiveEventsCount` may be absent. An id whose scope matches no tracked game is
// omitted from the response rather than zero-filled — a zero-filled row would assert a
// measured zero where there was no measurement.
export type BlueprintFamilyMonthlyTrendsItem = {
  id: string
  adoptionRate: BlueprintFamilyAdoptionMonthlyPoint[]
  density?: BlueprintFamilyTrendsMonthlyPoint[]
  activeLiveEventsCount?: BlueprintFamilyTrendsMonthlyPoint[]
}

// Response shape for POST /blueprint-families/{id}/trends. Mirrors BE
// BlueprintFamilyTrendsRespDto: the same `adoptionRate` and `density` lists as this
// family's item in `/trends/batch`, point for point.
export type BlueprintFamilyTrendsApiResponse = Pick<BlueprintFamilyMonthlyTrendsItem, 'adoptionRate' | 'density'>

export type GetGameQueryParams = {
  id: string
  marketIso: string
  gameOnly?: boolean
  tags: boolean
  userLanguage: UserLanguage
}

type GetGamesQueryParams = {
  ids: string[]
  userLanguage: UserLanguage
}

type GetGameVersionParams = {
  appId?: number
  marketIso?: string
  version?: string
}

type GetMultipleGameVersionParams = {
  apps: GetGameVersionParams[]
}

type GetRevenueAndDownloadAggregatesQueryParams = {
  appIds: number[]
  marketIsos: string[]
}

type GetTopFeaturesParams = {
  id: number
  genre: number
  marketIso: string | (string | null)[]
  store: string
  include: string
}

type CountryAggregate = {
  downloads30d: number
  downloads60d: number
  downloads90d: number
  downloads180d: number
  downloads360d: number
  downloadsLatest: number
  revenue30d: number
  revenue60d: number
  revenue90d: number
  revenue180d: number
  revenue360d: number
  revenueLatest: number
}
type RevenueAndDownloadAggregate = {
  appId: string
  countryAggregates: { [key: string]: CountryAggregate }
}
type RevenueAndDownloadAggregates = { [key: number]: RevenueAndDownloadAggregate }

type TopFeatures = {
  minEffect: number
  maxEffect: number
  avgEffect: number
}

type Language = {
  features: {
    [id: number]: {
      name: string
    }
  }
  categories: {
    [id: number]: {
      name: string
    }
  }
  tags: {
    [id: string]: {
      name: string
    }
  }
  tagGroups: {
    [id: string]: {
      name: string
    }
  }
}

export type CategoriesMap = {
  [id: string]: Category
}

type Category = {
  id: string
  active: boolean
  name: string
  ordr: number
  legacyId: number
  icon: {
    url: string
    mimeType: string
  }
  featureCount: number
  featureCounts: { [key: string]: number }
  features?: Feature[]
}

export type CategoriesFeatures = {
  [id: string]: Feature[]
}

export type ConventionalSubgenresMap = {
  [id: string]: {
    category: ConventionalCategory
    genre: ConventionalGenre
    subgenre: ConventionalSubgenre
  }
}

export type ConventionalGenresMap = {
  [id: string]: {
    category: ConventionalCategory
    genre: ConventionalGenre
  }
}

export type ConventionalCategoriesMap = {
  [id: string]: ConventionalCategory
}

type Feature = {
  id: string
  active: boolean
  choices: Choice[]
  hidden: boolean
  hideScreenshots: boolean
  legacyId: number
  name: string
  categoryId: string
  tags: string[]
  ordr: number
}

export type Choice = {
  active: boolean
  choice: string
  showInLists: boolean
  weightId: number
  ordr: number
}

type ScreenshotCounts = {
  [id: number]: number
}

type ScreenshotList = Screenshot[]

export type ScreenshotMap = {
  [country: string]: {
    [gameId: string]: {
      comments: number
      screenshots: number
    }
  }
}

type SimpleFeature = {
  choiceId?: string
  choiceLegacyId?: number
  choiceName?: string
  featureId?: string
  featureLegacyId: number
  featureName?: string
}

export type DetailedFeatureChoice = {
  active: boolean
  choice: string
  name: string
  ordr: number
  showInLists: boolean
  weightId: number
}

export type DetailedFeature = {
  active: boolean
  categoryId: string
  choices: DetailedFeatureChoice[]
  desc: string | null
  featureLegacyId: number
  featureName: string
  hidden: boolean
  hideScreenshots: boolean
  id: string
  legacyId: number
  markets: string[]
  name: string
  names: null
  ordr: number
  stores: string[] // ['565c92d39b75634154f213bc']
  tags: string[] // ['5e9d6c603ef43b00be56f344']
}

export type FileUploadResponse = {
  id: string
  url: string
  mimeType: string
}

type FileUpload = {
  body: FormData
}

export type LegacyFeatures = { [legacyId: string]: number }

type Screenshot = {
  screenshotId?: string
  commentId?: string | null
  commentPublished?: boolean
  features: SimpleFeature[] | LegacyFeatures | LegacyFeatures[]
  conceptIds: string[]
  id?: string
  gameId?: string
  gameVersion?: string
  url: string
  versionReleaseDate?: number
}

type ScreenshotComment = {
  analyst: Analyst
  screenshotComment: {
    analystId: string
    content: {
      comment: { [lang: string]: string }
    }
    gameId: string
    gameVersion?: string | null
    id: string
    published: boolean
    screenshotId: string
  }
}

export type KeywordList = Keyword[]

type Keyword = {
  id: string
  name: string
  linkedIds: number[]
  priority: number
}

type TagList = Tag[]

type Tag = {
  id: string
  name: string
  description: string | undefined
  imageText: string | undefined
  imageUrl: string | undefined
  targets: number[]
  active: boolean
  order: number
  type: 'feature' | 'game' | 'implementation' | 'chapter' | 'concept' | undefined
}

type ConceptTag = Tag &
  MongoDBObject & {
    concept: Concept
  }

type TagGroup = {
  id: string
  name: string
  tags: string[]
  order: number
  colorHex: string
  translations: {
    [key: string]: { name: string }
  }
}

type TagGroupList = TagGroup[]

export enum CollectionVisibility {
  public = 'PUBLIC',
  organization = 'ORGANIZATION',
  restricted = 'RESTRICTED',
}

type Collection = {
  allowedUsers: string[]
  createdAt: number
  createdBy: string
  id: string
  name: string
  screenshots: string[]
  visibility: CollectionVisibility
  modifiedAt?: number
}

type FtueVideoChapter = {
  chapterId: number
  dataSeek: number
  tags: string[]
  titles: { [lang: string]: string }
}

type FtueVideoThumbnail = {
  height: number
  link: string
  linkWithPlayButton: string
  width: number
}

export interface FtueVideo {
  chapters: FtueVideoChapter[]
  createdAt: number
  createdBy: string
  gameId: string
  gameVersion: string
  gameVersionReleaseDate: number
  id: string
  marketIso: string
  names: { [lang: string]: string }
  thumbnails: FtueVideoThumbnail[]
  type: string
  videoId: number
  videoName: string | null
  vimeoId: string
}

export type TrendingRequest = {
  conventionalGenreId?: string
  conventionalSubgenreId?: string
  enableCache: boolean
  endDate?: string | null // "2022-04-25"
  marketIso: string // "us"
  noChoices: boolean // false
  source: string // "revenue"
  startDate?: string | null // "2021-04-25"
  trendingDown: boolean // true
}

export type TrendingValue = {
  ts: number
  value: number
}

type TrendingFeature = {
  avgEffect: number //  0.16755443168107906
  categoryId: string // "565c92d39b75634154f213bf"
  choiceLabel: string // "Realistic"
  choiceLegacyId: number // 10
  conventionalCategory: string // "Casual"
  conventionalCategoryId: string // "5b87c6912f784f34cab23282"
  conventionalGenre: string // "AR/Location Based"
  conventionalGenreId: string // "5b87c6942f784f34cab23287"
  conventionalSubgenre: string // "AR/Location Based"
  conventionalSubgenreId: string // "5afc1e8e745d8d7640e9b1e6"
  featureId: string // "565c92d49b75634154f213df"
  featureLabel: string // "Artistic style"
  featureLegacyId: number // 5
  id: string // "6213abe456d3f4000123ef10"
  market: string // "565c92d39b75634154f213bb"
  marketIso: string // "us"
  maxEffect: number // 0.21312980900583967
  minEffect: number // 0.14476674301869874
  store: string // "565c92d39b75634154f213bc"
  visible: boolean // true
}

export type TrendingHistory = {
  [type: string]: TrendingValue[]
  [type: number]: TrendingValue[]
}

export type TrendingHistories = {
  [percentageGroup: string]: TrendingHistory
}

export type PercentageGroupValue = {
  [percentageGroup: string]: number
}

/** Note: Has more values, this is a reduced set */
export type PopularFeature = {
  choiceId: string
  choiceLabel: string // "6+"
  choiceLegacyId: number // 389
  conventionalCategoryId: string // ""
  conventionalGenreId: string
  conventionalSubgenreId: string
  featureId: string
  featureLabel: string
  featureLegacyId: number
  gamesInSet: PercentageGroupValue
  id: string
  market: string
  popularity: PercentageGroupValue
  trend: PercentageGroupValue
  visible: boolean
}

type FeatureFactsChoiceRequest = {
  choiceId: number
  conventionalCategoryId: string | null
  conventionalGenreId: string | null
  conventionalSubgenreId: string | null
  marketIso: string
  storeId: string
  userLanguage: UserLanguage
}

export type SaveLoginInfoResponse = {
  isFirst: boolean
}

export type FeatureFactsResponse = {
  count: {
    all: number
    out20Percent: number
    in20Percent: number
  }
  effects: { [key: string]: number }
  games: Game[]
  graph: {
    in20Percent: TrendingValue[]
    out20Percent: TrendingValue[]
  }
  minmaxFeature: TrendingFeature
  popularity: PopularFeature
}

export type TrendingResponse = {
  conventionalCategoryId: string | null
  conventionalGenreId: string | null
  conventionalSubgenreId: string | null
  histories: TrendingHistories
  market: string
  minmaxFeatures: { [choiceId: string]: TrendingFeature }
  popularFeatures: PopularFeature[]
  source: string
}

type GameCompetitorsResponse = {
  competitors: { [numberString: string]: Game[] }
  ranks: { [marketIso: string]: number }
}

export type DemographyGroup = {
  demographicsAge16_24?: number
  demographicsAge25_44?: number
  demographicsAge45?: number
  demographicsFemale?: number
  demographicsMale?: number
}

type FeatureDemographic = {
  choiceLegacyId: number
  demographics: DemographyGroup
  featureLegacyId: number
  id: string
  marketIso: string
}

export type CompareMultiGames = {
  games: Game[]
  features: CompareGameFeature[]
  categories: {
    [categoryId: string]: number[]
  }
}

export type CompareGameFeature = {
  featureName: string
  featureId: number
  categoryName: string
  categoryId: string
  similar: boolean
  order: number
  choices: string[]
  choiceIds: number[]
  effects: number[]
}

type MotivationProfileQueryParam = { id: number | string; isOwnGame?: boolean }
type MotivationProfileHistoryQueryParam = { appId: number }
type MotivationProfileHistoryVersionQueryParam = MotivationProfileHistoryQueryParam & { version: string }
export type MotivationStatsQueryParam = { appId: number; conventionalSubgenreId: string; gameId?: string }

export type Subgenre = {
  id: string
  createdAt: number
  modifiedAt: number
  createdBy: string
  modifiedBy: string
  name: string
  parentGenre: string
  parentGenreId: string
  parentCategory?: string
  rules: { [key: number]: number[] }
  description: string
  imageUrl: string
  imageText: string
  exampleAppIds: number[]
}

export type Genre = {
  id: string
  createdAt: number
  modifiedAt: number
  createdBy: string
  modifiedBy: string
  name: string
  parentGenre: string
  parentGenreId: string
  parentCategory?: string
  rules: { [key: number]: number[] }
  description: string
  imageUrl: string
  imageText: string
  exampleAppIds: number[]
  subgenres: string[]
}

type TrackedGamesListResponse = { gamesWithPermission: TrackedGame[]; gamesWithoutPermission: TrackedGame[] }

const REDUCER_PATH = 'coreApi'

// Catalogue list / rollup / momentum are reused when navigating catalogue →
// blueprint detail → back. RTK's 60s default drops those fat payloads while the
// user is still reading the detail page. Five minutes keeps the round-trip a
// cache hit without pinning the catalogue forever.
export const BLUEPRINT_FAMILY_KEEP_UNUSED_DATA_FOR_SECONDS = 300

// A blueprint block that is collapsed and opened again within this time reuses its game's performance window.
export const TRACKED_GAME_PERFORMANCE_KEEP_UNUSED_DATA_FOR_SECONDS = 300

export const coreApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: fetchBaseQuery({
    baseUrl: window.GR_API_URLS.API_URL_CORE,
    prepareHeaders: prepareDefaultHeaders,
  }),
  tagTypes: ['Collection', 'Game', 'Games', 'GameAndAnalysis', 'GameAnalysis', 'AnalysisNote', 'Bookmarks', 'Concepts', 'GamesWithFilter', 'BlueprintFamilies'],
  endpoints: (builder) => ({
    getGames: builder.query<Game[], GetGamesQueryParams>({
      query: ({ ids = [] }) => `games/${sanitizeGameIds(ids).join(',')}`,
      transformResponse: (games: Game[], _, { userLanguage }) => {
        return games.map((game) => new Game(game, { userLanguage }))
      },
    }),
    getGame: builder.query<Game, GetGameQueryParams>({
      query: ({ id, ...rest }) => ({ url: `games/${id}`, params: { ...rest, gameOnly: true } }),
      transformResponse: (response: { game: Game }, _, { userLanguage }) => {
        return new Game(response.game, { userLanguage })
      },
      providesTags: (result, err, { id }) => [{ type: 'Game', id }],
    }),
    getGameAndAnalysis: builder.query<GameAndAnalysis, GetGameQueryParams>({
      query: ({ id, ...rest }) => ({ url: `games/${id}`, params: { ...rest, gameOnly: false } }),
      transformResponse: (response: GameAndAnalysis, _, { userLanguage }) => {
        return {
          ...response,
          game: new Game(response.game, { userLanguage }),
        }
      },
      providesTags: (result, err, { id }) => [
        { type: 'GameAndAnalysis', id },
        { type: 'Games', id },
      ],
    }),
    getMultipleGamesAndAnalysis: builder.query<GameAndAnalysis[], { queryParams: GetGameQueryParams[] }>({
      async queryFn(args, queryApi, extraOptions, fetchWithBQ) {
        const responses = await Promise.all(
          args.queryParams.map(async ({ id, ...rest }) => fetchWithBQ({ url: `/games/${id}`, params: { ...rest, gameOnly: false } }))
        )

        // Filter out failed responses and only process successfully loaded games
        const successfulResponses = responses.filter((res) => !res.error && res.data)

        const data = successfulResponses.map((res) => {
          const gameAndAnalysis = res.data as GameAndAnalysis
          const game = new Game(gameAndAnalysis.game, { userLanguage: args.queryParams.find((qp) => qp.id === gameAndAnalysis.game.id)?.userLanguage })
          return { ...gameAndAnalysis, game: game } as GameAndAnalysis
        })

        // Return successfully loaded games even if some failed
        return { data }
      },
    }),
    getGameList: builder.query<Game[], { gameIds: (string | number)[]; include?: string; userLanguage: UserLanguage }>({
      query: ({ gameIds, include }) => ({
        url: 'games/list',
        method: 'POST',
        body: {
          gameIds,
          include: include || '',
        },
      }),
      transformResponse: (games: Game[], _, { gameIds, userLanguage }) =>
        games.map((game) => new Game(game, { userLanguage })).sort((a, b) => gameIds.indexOf(a.id) - gameIds.indexOf(b.id)),
    }),
    getGameInnovatorList: builder.query<GenreInnovatorGame[], { conventionalGenreId: string; fields: string[]; userLanguage: UserLanguage }>({
      query: ({ userLanguage, ...body }) => ({
        url: 'games/innovator/list',
        method: 'POST',
        body,
      }),
      transformResponse: (games: GenreInnovatorGame[], _, { userLanguage }) => {
        return games.map((game) => new GenreInnovatorGame(game, { userLanguage }))
      },
    }),
    getSoftLaunchGames: builder.query<Game[], { conventionalSubgenreIds: string[]; marketIso: string; includes: string[]; userLanguage: UserLanguage }>({
      query: ({ includes, marketIso, conventionalSubgenreIds }) => ({
        url: 'games/softlaunch',
        method: 'POST',
        body: {
          marketIso,
          conventionalSubgenreIds,
          include: includes.join(','),
        },
      }),
      transformResponse: (games: Game[], _, { userLanguage }) => games.map((game) => new Game(game, { userLanguage })),
    }),
    getScreenshotComment: builder.query<ScreenshotComment, string>({
      query: (commentId) => `screenshotcomments/${commentId}`,
    }),
    getScreenshotsByIds: builder.query<Screenshot[], string[]>({
      query: (screenshotIds) => ({
        url: `screenshots/list`,
        method: 'POST',
        body: screenshotIds,
      }),
    }),
    getScreenshotsByGameIds: builder.query<Screenshot[], string[]>({
      query: (gameIds) => ({
        url: `screenshots/list/games`,
        method: 'POST',
        body: gameIds,
      }),
    }),
    getScreenshotsByConceptIds: builder.query<Screenshot[], string[]>({
      query: (conceptIds) => ({
        url: `/screenshots/concepts/${conceptIds}`,
        method: 'GET',
      }),
    }),
    getSetting: builder.query<Setting, string>({
      query: (settingKey) => `settings/${settingKey}`,
    }),
    getFeatureEffects: builder.query<FeaturesEffect, void>({
      query: () => `features_effects`,
    }),
    getRevenueAndDownloadAggregates: builder.query<RevenueAndDownloadAggregates, GetRevenueAndDownloadAggregatesQueryParams>({
      query: (params) => ({
        url: '/games/revenue_downloads/aggregates',
        method: 'POST',
        body: params,
      }),
    }),
    getGamesByStage: builder.query<Game[], GetGamesByStageQueryParams>({
      query: ({ marketIso, stageId, ...queryParams }) => ({
        url: `/games/stage/${marketIso}/${stageId}`,
        params: queryParams,
      }),
      transformResponse: (response: Game[], _, { userLanguage }) => response.map((game) => new Game(game, { userLanguage })),
    }),
    sendGameAnalysisSuggestion: builder.mutation<void, GameAnalysisSuggestion>({
      query: ({ ...gameAnalysisSuggestion }) => ({
        url: `games/actions/analysis-request`,
        method: 'PUT',
        body: gameAnalysisSuggestion,
      }),
    }),
    getGameVersionInfo: builder.query<VersionInfo, GetGameVersionParams>({
      query: ({ appId, marketIso, version }) => ({
        url: `/versions/${appId}${version ? '/' + version : ''}`,
        params: { marketIso },
      }),
    }),
    getGameVersionInfoForVersion: builder.query<VersionInfo, { appId: number | undefined; version: string; marketIso: string | undefined }>({
      query: ({ appId, version, marketIso }) => ({
        url: `/versions/${appId}/${version}`,
        params: { marketIso },
      }),
    }),
    getGameVersionInfoList: builder.query<VersionInfo[], { appId: number | undefined; marketIso: string | undefined }>({
      query: ({ appId, marketIso }) => ({
        url: `/versions/${appId}/list`,
        params: { marketIso },
      }),
    }),
    getMultipleGameVersionInfoList: builder.query<VersionInfo[], GetMultipleGameVersionParams>({
      async queryFn(args, queryApi, extraOptions, fetchWithBQ) {
        const responses = await Promise.all(
          args.apps.map(async ({ appId, marketIso, version }) => fetchWithBQ({ url: `/versions/${appId}/list`, params: { marketIso } }))
        )
        // Tag each version with the market it was requested for so consumers can tell the same
        // app's versions apart when it is queried for multiple markets at once (GR-1332)
        const data = responses
          .map((resp, index) =>
            ((resp.data as VersionInfo[]) || []).map((versionInfo) => ({ ...versionInfo, market: args.apps[index].marketIso ?? versionInfo.market }))
          )
          .flat()
          .sort((a, b) => a.releaseDate - b.releaseDate)

        const errorResponse = responses.find((r) => r.error)
        return errorResponse?.error ? { error: errorResponse.error } : { data }
      },
    }),
    getFeatureFactsChoice: builder.query<FeatureFactsResponse, FeatureFactsChoiceRequest>({
      query: ({ userLanguage, ...body }) => ({
        url: `/features/facts`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: FeatureFactsResponse, _, { userLanguage }) => ({
        ...response,
        games: response.games.map((game) => new Game(game, { userLanguage })),
      }),
    }),
    getFeature: builder.query<DetailedFeature, number>({
      query: (legacyId) => ({
        url: `/features/${legacyId}`,
      }),
      transformResponse: (response: DetailedFeature) => {
        return {
          ...response,
          featureName: languageService.getTranslation('features', response.legacyId.toString()),
          choices: response.choices.map((choice) => ({
            ...choice,
            name: languageService.getTranslation('choices', choice.weightId.toString()),
          })),
        }
      },
    }),
    getActiveFeatureList: builder.query<number[], void>({
      query: () => ({
        url: `/features/list/active/legacyIds`,
      }),
    }),
    getTopFeatures: builder.query<TopFeatures, GetTopFeaturesParams>({
      query: ({ id, genre, ...queryParams }) => ({
        url: `/top-features/${id}/genre/${genre}`,
        params: queryParams,
      }),
    }),
    getFeatureByConventionalSubgenre: builder.query<TopFeatures, GetTopFeaturesParams>({
      query: ({ id, genre, ...queryParams }) => ({
        url: `/top-features/${id}/conventionalSubgenre/${genre}`,
        params: queryParams,
      }),
    }),
    searchGames: builder.mutation<Game[], SearchGamesQueryParams>({
      query: ({ userLanguage, ...searchGamesParams }) => ({
        url: `games/search`,
        method: 'POST',
        body: searchGamesParams,
        params: { enableCache: true },
      }),
      transformResponse: (games: Game[], _, { userLanguage }) => games.map((game) => new Game(game, { userLanguage })),
    }),
    searchArtist: builder.query<string[], SearchArtistQueryParams>({
      query: (queryParams) => ({
        url: `games/artist/search/${encodeURI(queryParams.term)}`,
        method: 'GET',
      }),
    }),
    getGameUpdateImpacts: builder.query<UpdateImpact[], GetGameUpdateImpactsQueryParams>({
      query: (queryParams) => ({
        url: '/impacts/list',
        params: queryParams,
      }),
      transformResponse: (updateImpacts: UpdateImpact[]) =>
        updateImpacts.map((updateImpact) => {
          return {
            ...updateImpact,
            conventionalSubgenre: languageService.getTranslation('conventionalSubgenres', updateImpact.conventionalSubgenreId) || '',
            conventionalGenre: languageService.getTranslation('conventionalGenres', updateImpact.conventionalGenreId) || '',
            conventionalCategory: languageService.getTranslation('conventionalSubgenres', updateImpact.conventionalCategoryId) || '',
          }
        }),
    }),
    getCompareMultiGames: builder.query<CompareMultiGames, GetCompareMultiGamesParams>({
      query: ({ marketIso, games }) => ({
        url: `/games/comparemulti/${marketIso.join(',')}/${games.join(',')}`,
      }),
      transformResponse: (response: CompareMultiGames, _, { userLanguage }) => ({
        ...response,
        features: response.features.map((feature) => {
          feature.featureName = languageService.getTranslation('features', feature.featureId.toString())
          return feature
        }),
        games: response.games.map((game) => new Game(game, { userLanguage })),
      }),
    }),
    getAnalysisComment: builder.query<AnalystComment, string>({
      query: (commentId) => ({
        url: `/analysiscomments/${commentId}`,
      }),
    }),
    getGamesWithFilter: builder.query<Game[], GetGamesWithFilterQueryParams>({
      query: (queryParams) => ({
        url: `/games`,
        params: queryParams,
      }),
      providesTags: ['Games', 'GamesWithFilter'],
      transformResponse: (response: Game[], _, { userLanguage }) => {
        return response.map((game) => {
          return new Game(game, { userLanguage })
        })
      },
    }),
    getUpdateImpactsForGameInMarket: builder.query<UpdateImpact[], GetGameUpdateImpactsForMarketQueryParams>({
      query: ({ appId, ...queryParams }) => ({
        url: `/impacts/market/${appId}`,
        params: queryParams,
      }),
      transformResponse: (updateImpacts: UpdateImpact[]) =>
        updateImpacts.map((updateImpact) => {
          return {
            ...updateImpact,
            conventionalSubgenre: languageService.getTranslation('conventionalSubgenres', updateImpact.conventionalSubgenreId) || '',
            conventionalGenre: languageService.getTranslation('conventionalGenres', updateImpact.conventionalGenreId) || '',
            conventionalCategory: languageService.getTranslation('conventionalSubgenres', updateImpact.conventionalCategoryId) || '',
          }
        }),
    }),
    getUpdateImpactsForMultipleGamesInMarket: builder.query<UpdateImpact[], GetMultipleGamesUpdateImpactsForMarketQueryParams>({
      async queryFn(args, queryApi, extraOptions, fetchWithBQ) {
        const responses = await Promise.all(
          args.apps.map(async ({ appId, marketIso }) => fetchWithBQ({ url: `/impacts/market/${appId}`, params: { marketIso } }))
        )
        // Tag each impact with the market it was requested for so consumers can tell the same
        // app's impacts apart when it is queried for multiple markets at once (GR-1332)
        const data = responses
          .map((res, index) =>
            ((res.data as UpdateImpact[]) || []).map((updateImpact) => ({ ...updateImpact, market: args.apps[index].marketIso ?? updateImpact.market }))
          )
          .flat()
          .sort((a, b) => a.versionReleaseDate - b.versionReleaseDate)
        const errorResponse = responses.find((r) => r.error)
        return errorResponse?.error ? { error: errorResponse.error } : { data }
      },
    }),
    getUpdateImpactForGameVersion: builder.query<UpdateImpact, { appId: number; version: string; marketIso: string }>({
      query: ({ appId, version, marketIso }) => ({
        url: `/impacts/market/${appId}/${version}`,
        params: {
          marketIso,
        },
      }),
    }),
    getEstimatesList: builder.query<EstimatesList, GetEstimatesListQueryParams>({
      query: (queryParams) => ({
        url: '/estimates/list',
        method: 'POST',
        body: queryParams,
      }),
    }),
    getMarkets: builder.query<Market[], { userLanguage: UserLanguage }>({
      query: () => 'markets',
      transformResponse: (response: Market[], _, userLanguage) => {
        const markets = response
          .map((market) => {
            return { ...market, name: languageService.getTranslation('markets', market.iso) }
          })
          .sort((a, b) => (a.name < b.name ? -1 : 1))
        return markets
      },
    }),
    getBlueprintFamilies: builder.query<BlueprintFamilyApiItem[], { windowDays?: BlueprintFamilyTrendsWindowDays } & ConventionalScope>({
      keepUnusedDataFor: BLUEPRINT_FAMILY_KEEP_UNUSED_DATA_FOR_SECONDS,
      // POST so segment scoping can be sent as a `conventionalSubgenreIds` body list.
      // `windowDays` scopes `cohortGamesCount`, `distinctGamesCount`, `adoptionRate.windowed`,
      // `newGames` and uplift alike. Body is @NotNull on the BE, so always send an array
      // (empty = whole catalogue).
      //
      // No endpoint under /blueprint-families returns discontinued families any more, so
      // there is nothing to toggle. The internal GET /i/blueprint-families still lists
      // them but is role-restricted and returns a different shape.
      query: ({ windowDays, ...scope }) => ({
        url: 'blueprint-families',
        method: 'POST',
        params: {
          ...(windowDays !== undefined ? { windowDays } : {}),
        },
        body: buildConventionalScopeBody(scope),
      }),
      providesTags: (_result, _err, { windowDays, ...scope }) => [
        {
          type: 'BlueprintFamilies',
          id: `list/${windowDays ?? 'default'}/${conventionalScopeKey(scope)}`,
        },
      ],
    }),
    getBlueprintFamilyGlossary: builder.query<BlueprintFamilyGlossaryItem[], void>({
      // A large payload (descriptions and every example screenshot) that rarely changes. RTK's 60s
      // default drops it shortly after the Data Glossary tab is left, so a revisit refetched it.
      keepUnusedDataFor: BLUEPRINT_FAMILY_KEEP_UNUSED_DATA_FOR_SECONDS,
      query: () => ({ url: 'blueprint-families/glossary' }),
      providesTags: () => [{ type: 'BlueprintFamilies', id: 'glossary' }],
    }),
    getBlueprintFamily: builder.query<
      BlueprintFamilyDetailApiItem,
      { blueprintFamilyId: string; windowDays?: BlueprintFamilyTrendsWindowDays; conventionalSubgenreIds?: string[] }
    >({
      // POST so the adoption-rate denominator can be scoped via a `conventionalSubgenreIds` body list.
      query: ({ blueprintFamilyId, windowDays, conventionalSubgenreIds }) => ({
        url: `blueprint-families/${blueprintFamilyId}`,
        method: 'POST',
        params: {
          ...(windowDays !== undefined ? { windowDays } : {}),
        },
        body: { conventionalSubgenreIds: conventionalSubgenreIds ?? [] },
      }),
      // Encode the window + subgenre scope in the tag (mirrors `getBlueprintFamilyTrends`)
      // so a scoped snapshot is cached/invalidated distinctly from the market-wide one.
      providesTags: (_result, _err, { blueprintFamilyId, windowDays, conventionalSubgenreIds }) => [
        {
          type: 'BlueprintFamilies',
          id: `${blueprintFamilyId}/${windowDays ?? 'default'}/${[...(conventionalSubgenreIds ?? [])].sort().join(',') || 'all'}`,
        },
      ],
    }),
    // This list is all-time by design — every game that has ever run the family — so it
    // includes discontinued blueprints AND discontinued games, unlike the metric endpoints
    // which still exclude them.
    //
    // Each row states both retirement flags itself — `discontinued` for the blueprint and
    // `gameDiscontinued` for the game — so the table no longer joins `/games/tracked/list`
    // to find out. They are independent; all four combinations occur on prod.
    //
    // `includePcConsole: true` adds the PC/console game-level blueprints to the same
    // list (default: mobile only). The rows carry no game type, so callers that need
    // to tell the two apart diff this against the mobile-only list.
    getBlueprints: builder.query<BlueprintApiItem[], { blueprintFamilyId: string; windowDays?: BlueprintFamilyTrendsWindowDays; includePcConsole?: boolean }>({
      query: ({ blueprintFamilyId, windowDays, includePcConsole }) => ({
        url: `blueprint-families/${blueprintFamilyId}/blueprints`,
        params: {
          ...(windowDays !== undefined ? { windowDays } : {}),
          ...(includePcConsole !== undefined ? { includePcConsole } : {}),
        },
      }),
      providesTags: (_result, _err, { blueprintFamilyId, includePcConsole }) => [
        { type: 'BlueprintFamilies', id: `${blueprintFamilyId}/blueprints${includePcConsole ? '/pc-console' : ''}` },
      ],
    }),
    // A PC/console game-level blueprint is rejected unless `includePcConsole` is true.
    getBlueprint: builder.query<
      BlueprintDetailApiItem,
      { blueprintFamilyId: string; blueprintId: string; windowDays?: BlueprintFamilyTrendsWindowDays; includePcConsole?: boolean }
    >({
      query: ({ blueprintFamilyId, blueprintId, windowDays, includePcConsole }) => ({
        url: `blueprint-families/${blueprintFamilyId}/blueprints/${blueprintId}`,
        params: {
          ...(windowDays !== undefined ? { windowDays } : {}),
          ...(includePcConsole !== undefined ? { includePcConsole } : {}),
        },
      }),
      providesTags: (_result, _err, { blueprintFamilyId, blueprintId, windowDays, includePcConsole }) => [
        {
          type: 'BlueprintFamilies',
          id: `${blueprintFamilyId}/blueprints/${blueprintId}/${windowDays ?? 'default'}${includePcConsole ? '/pc-console' : ''}`,
        },
      ],
    }),
    getBlueprintFamilyTrends: builder.query<
      BlueprintFamilyTrendsApiResponse,
      { blueprintFamilyId: string; windowDays?: BlueprintFamilyTrendsWindowDays; conventionalSubgenreIds?: string[] }
    >({
      // POST so the adoption-rate denominator can be scoped via a `conventionalSubgenreIds` body list.
      query: ({ blueprintFamilyId, windowDays, conventionalSubgenreIds }) => ({
        url: `blueprint-families/${blueprintFamilyId}/trends`,
        method: 'POST',
        params: {
          ...(windowDays !== undefined ? { windowDays } : {}),
        },
        body: { conventionalSubgenreIds: conventionalSubgenreIds ?? [] },
      }),
      providesTags: (_result, _err, { blueprintFamilyId, windowDays, conventionalSubgenreIds }) => [
        {
          type: 'BlueprintFamilies',
          id: `${blueprintFamilyId}/trends/${windowDays ?? 'default'}/${[...(conventionalSubgenreIds ?? [])].sort().join(',') || 'all'}`,
        },
      ],
    }),
    getStores: builder.query<Store[], void>({
      query: () => 'stores',
    }),
    getGenreTaxonomy: builder.query<ConventionalCategory[], { userLanguage: UserLanguage }>({
      query: () => `conventional/map`,
      transformResponse: (
        response: { [key: string]: { [key: string]: Array<string> } },
        _,
        { userLanguage /* Enables Localization when user language changes */ }
      ) => {
        const priorityMap: { [categoryId: string]: number } = {
          '5b87c6912f784f34cab23282': 1, // Casual
          '5b87c6902f784f34cab23280': 2, // Mid-core
          '5b87c6992f784f34cab2328b': 3, // Sports & Driving
          '5b87c69b2f784f34cab2328e': 4, // Casino
        }

        return Object.keys(response)
          .map((categoryId) => ({
            name: languageService.getTranslation('conventionalCategories', categoryId),
            id: categoryId,
            genres: Object.keys(response[categoryId])
              .map((genreId) => ({
                name: languageService.getTranslation('conventionalGenres', genreId),
                id: genreId,
                categoryId: categoryId,
                subgenres: response[categoryId][genreId]
                  .map((subgenreId) => ({
                    name: languageService.getTranslation('conventionalSubgenres', subgenreId),
                    id: subgenreId,
                    genreId: genreId,
                    categoryId: categoryId,
                  }))
                  .sort(compareNames),
              }))
              .sort(compareNames),
            priority: priorityMap[categoryId] || 100,
          }))
          .sort(comparePriority)
      },
    }),
    getConventionalGenreList: builder.query<Genre[], { userLanguage: UserLanguage }>({
      query: () => `/conventional/genre/list`,
      transformResponse: (response: Genre[], _, userLanguage) => {
        return response.map((genre) => {
          return {
            ...genre,
            name: languageService.getTranslation('conventionalGenres', genre.id),
            description:
              languageService.getTranslation('conventionalGenres', genre.id, 'description', true) !== ''
                ? languageService.getTranslation('conventionalGenres', genre.id, 'description')
                : genre.description,
          }
        })
      },
    }),
    getConventionalSubgenreList: builder.query<Subgenre[], { userLanguage: UserLanguage }>({
      query: () => `/conventional/subgenre/list`,
      transformResponse: (response: Subgenre[], _, userLanguage) => {
        return response.map((subgenre) => {
          return {
            ...subgenre,
            name: languageService.getTranslation('conventionalSubgenres', subgenre.id),
            parentGenre: languageService.getTranslation('conventionalGenres', subgenre.parentGenreId),
            description:
              languageService.getTranslation('conventionalSubgenres', subgenre.id, 'description', true) !== ''
                ? languageService.getTranslation('conventionalSubgenres', subgenre.id, 'description')
                : subgenre.description,
          }
        })
      },
    }),
    getLanguageMap: builder.query<LanguageMap, string>({
      query: (language) => `language/${language}`,
    }),
    getLanguage: builder.query<Language, { language: string }>({
      query: ({ language }) => ({
        url: `/language/${language}`,
      }),
    }),
    getCategoriesMap: builder.query<CategoriesMap, { marketIso: string }>({
      query: ({ marketIso }) => ({
        url: `/categories/map/${marketIso}`,
      }),
      transformResponse: (categoriesMap: CategoriesMap) => {
        return Object.entries(categoriesMap).reduce((acc, [categoryId, category]) => {
          acc[categoryId] = {
            ...category,
            name: languageService.getTranslation('categories', category.legacyId + ''),
          }

          return acc
        }, {} as CategoriesMap)
      },
    }),
    getCategoriesFeatures: builder.query<CategoriesFeatures, { categories: string[]; marketIso: string }>({
      async queryFn(args, queryApi, extraOptions, fetchWithBQ) {
        const responses = await Promise.all(args.categories.map(async (category) => fetchWithBQ({ url: `/categories/${category}/features/${args.marketIso}` })))
        const errorResponse = responses.find((r) => r.error)
        return errorResponse?.error
          ? { error: errorResponse.error }
          : { data: Object.fromEntries(args.categories.map((c, i) => [c, responses[i].data as Feature[]])) }
      },
    }),
    getScreenshotList: builder.query<ScreenshotList, string[]>({
      query: (screenshotIds) => ({
        url: 'screenshots/list',
        method: 'POST',
        body: screenshotIds,
      }),
      transformResponse: (response: ScreenshotList, meta, screenshotsIds) => {
        if (screenshotsIds.length !== response.length) {
          throw new Error(`Expected ${screenshotsIds.length} screenshots, but got ${response.length}`)
        }

        return Object.values(response).map((screenshot) => ({
          ...screenshot,
          features: Object.entries(screenshot.features).map(
            ([featureId, choiceId]) =>
              ({
                featureLegacyId: parseInt(featureId, 10),
                featureName: languageService.getTranslation('features', featureId),
                choiceLegacyId: choiceId as number,
                choiceName: languageService.getTranslation('choices', choiceId),
              } as SimpleFeature)
          ),
        }))
      },
    }),
    getScreenshotsByFeatures: builder.query<ScreenshotList, { choices: number[] }>({
      query: ({ choices }) => ({
        url: `/screenshots/features?choices=${choices.join(',')}`,
      }),
      transformResponse: (response: ScreenshotList) => {
        // Sort the screenshots by their ID in ascending order
        response.sort((a, b) => (a.id as string).localeCompare(b.id as string))
        return response
      },
    }),
    getScreenshotCounts: builder.query<ScreenshotCounts, { featureLegacyId?: number }>({
      query: ({ featureLegacyId }) => ({
        url: `/screenshots/count${featureLegacyId ? `?featureLegacyId=${featureLegacyId}` : ''}`,
      }),
    }),
    getScreenshotCountsByGames: builder.query<{ [gameId: string]: ScreenshotCounts }, { gameIds: string[] }>({
      async queryFn(args, queryApi, extraOptions, fetchWithBQ) {
        const responses = await Promise.all(args.gameIds.map(async (gameId) => fetchWithBQ({ url: `/screenshots/count/${gameId}` })))
        const errorResponse = responses.find((r) => r.error)
        return errorResponse?.error
          ? { error: errorResponse.error }
          : {
              data: args.gameIds.reduce(
                (acc, gameId, i) => ({
                  ...acc,
                  [gameId]: responses[i].data as ScreenshotCounts,
                }),
                {}
              ),
            }
      },
    }),
    getScreenshotCountsByGameAndCountry: builder.query<ScreenshotMap, void>({
      query: () => ({
        url: `/screenshots/map`,
      }),
    }),
    getImageObjectUrl: builder.query<string, { url: string }>({
      query: ({ url }) => {
        const originalUrl = new URL(url)
        return {
          url: originalUrl.pathname.replace('/v2/', '/'),
          headers: {
            Accept: '*/*',
          },
          responseHandler: async (response) => URL.createObjectURL(await response.blob()),
        }
      },
    }),
    getKeywordList: builder.query<KeywordList, { type: string; userLanguage?: UserLanguage }>({
      query: ({ type }) => ({
        url: `/keywords/list/${type}`,
      }),
      transformResponse: (response: Keyword[], _, userLanguage) => {
        return response.map((keyword) => {
          return {
            ...keyword,
            name: languageService.getTranslation('keywords', keyword.id, 'name'),
          }
        })
      },
    }),
    getTagList: builder.query<TagList, { type: string; userLanguage: UserLanguage }>({
      query: ({ type }) => ({
        url: `/tags/list?type=${type}`,
      }),
      transformResponse: (response: Tag[], _, { userLanguage }) => {
        return response.map((tag) => {
          return {
            ...tag,
            name: languageService.getTranslation('tags', tag.id, 'name'),
          }
        })
      },
    }),
    getTagGroupsList: builder.query<TagGroupList, { type: string; userLanguage: UserLanguage }>({
      // Reuses the blueprint-family 5 minute window (not family-specific): the Data Glossary tab needs these groups on every visit.
      keepUnusedDataFor: BLUEPRINT_FAMILY_KEEP_UNUSED_DATA_FOR_SECONDS,
      query: ({ type }) => ({
        url: `/tag-groups/list`,
        params: {
          type,
        },
      }),
      transformResponse: (response: TagGroup[], _, { userLanguage }) => {
        return response
          .map((tagGroup) => {
            return {
              ...tagGroup,
              name: languageService.getTranslation('tagGroups', tagGroup.id, 'name'),
            }
          })
          .sort((a, b) => a.order - b.order)
      },
    }),
    getVisibleCollections: builder.query<Collection[], void>({
      query: () => ({
        url: `/pinnedscreenshots/listvisible`,
      }),
      providesTags: ['Collection'],
    }),
    updateCollection: builder.mutation<void, Pick<Collection, 'id' | 'allowedUsers' | 'name' | 'visibility' | 'screenshots'>>({
      query: (collection) => ({
        url: `/pinnedscreenshots/${collection.id}`,
        method: 'PUT',
        body: collection,
      }),
      invalidatesTags: ['Collection'],
    }),
    createCollection: builder.mutation<void, Pick<Collection, 'allowedUsers' | 'name' | 'visibility' | 'screenshots'>>({
      query: (collection) => ({
        url: `/pinnedscreenshots`,
        method: 'POST',
        body: collection,
      }),
      invalidatesTags: ['Collection'],
    }),
    deleteCollection: builder.mutation<void, string>({
      query: (collectionId) => ({
        url: `/pinnedscreenshots/${collectionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Collection'],
    }),
    getGameReview: builder.query<GameReview, string>({
      query: (reviewId) => `/gamereviews/${reviewId}`,
    }),
    startIntercomConversation: builder.query<{}, IntercomConversationQueryParams>({
      query: (conversation) => ({
        url: `/conversations/start`,
        method: 'POST',
        body: conversation,
      }),
    }),
    getLatestFtueVideos: builder.query<FtueVideo[], void>({
      query: () => `/ftue-videos/list/latest/?type=ftue`,
    }),
    getLatestFtueVideosByGameIds: builder.query<FtueVideo[], { gameIds: string[] }>({
      query: ({ gameIds }) => ({
        url: `/ftue-videos/list`,
        method: 'POST',
        body: gameIds,
      }),
    }),
    getFtueVideosByGame: builder.query<FtueVideo[], string>({
      query: (gameId) => `/ftue-videos/game/${gameId}`,
      transformResponse: (response: FtueVideo[]) => {
        // sort videos from latest to oldest
        return response.sort((a, b) => {
          return b.createdAt - a.createdAt
        })
      },
    }),
    getTutorialVideos: builder.query<FtueVideo[], void>({
      query: () => `/ftue-videos/?type=onboarding`,
    }),
    postUploadFile: builder.mutation<FileUploadResponse, FileUpload>({
      query: ({ body }) => ({
        url: `/files/upload/private`,
        method: 'POST',
        body,
      }),
    }),
    postCreateNewGame: builder.mutation<Game, NewGameMetaData>({
      query: (NewGameMetaData) => ({
        url: `/games`,
        method: 'POST',
        body: NewGameMetaData,
      }),
      invalidatesTags: ['Games'],
    }),
    updateGame: builder.mutation<Game, { game: Game; gameId: string }>({
      query: ({ game, gameId }) => ({
        url: `/games/${gameId}`,
        method: 'PUT',
        body: game,
      }),
      invalidatesTags: ['Games', 'Game'],
    }),
    deleteGame: builder.mutation<Game, string>({
      query: (gameId) => ({
        url: `/games/${gameId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Games'],
    }),
    cloneGame: builder.mutation<Game, { gameId: string; userLanguage: UserLanguage }>({
      query: ({ gameId }) => ({
        url: `/games/clone/${gameId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Games'],
      transformResponse: (response: Game, _, { userLanguage }) => new Game(response, { userLanguage }),
    }),
    getGameCompetitors: builder.query<Game[], { gameId: string; marketIso: string; userLanguage: UserLanguage }>({
      query: ({ gameId, marketIso }) => ({
        url: `/games/${gameId}/competitors/${marketIso}`,
      }),
      transformResponse: (response: GameCompetitorsResponse, _, { userLanguage }) => {
        let games: Game[] = []
        let competitorOrder = 1
        Object.values(response.competitors).forEach((gameArray) => {
          gameArray.forEach((gameData) => {
            const game = new Game(gameData, { userLanguage })
            game.competitorOrder = competitorOrder
            games.push(game)
            competitorOrder++
          })
        })
        return games
      },
    }),
    getMarketFeatures: builder.query<Feature[], { marketIso: string; featureLegacyIds: number[] }>({
      query: ({ marketIso, featureLegacyIds }) => ({
        url: `/features/market/${marketIso}?featureLegacyIds=${featureLegacyIds.join(',')}`,
      }),
    }),
    getAllMarketFeatures: builder.query<Feature[], { marketIso: string }>({
      query: ({ marketIso }) => ({
        url: `/features/market/${marketIso}`,
      }),
    }),
    getExplorerFeaturesForConventionalSubgenres: builder.query<
      TopFeature[],
      { marketIso: string; selectedGameId?: string; conventionalSubgenreIds?: string[] }
    >({
      query: ({ marketIso, selectedGameId, conventionalSubgenreIds }) => ({
        url: '/top-features/explorer/market',
        method: 'POST',
        body: {
          marketIso,
          conventionalSubgenreIds: conventionalSubgenreIds?.join(','),
          selectedGameId,
        },
      }),
      transformResponse: (topFeatures: TopFeature[]) => topFeatures.map((topFeature) => featureService.localizeFeature(topFeature)) as TopFeature[],
    }),
    getAnalysisFeatureChoices: builder.query<FeatureChoice[], { analysisId: string; marketIso: string; storeId: string }>({
      query: ({ analysisId, marketIso, storeId }) => ({
        url: `/top-features/analysis/${analysisId}`,
        params: {
          storeId,
          marketIso,
        },
      }),
      transformResponse: (featureChoices: FeatureChoice[]) =>
        featureChoices.map((featureChoice) => featureService.localizeFeature(featureChoice)) as FeatureChoice[],
    }),
    getGameLatestAnalysis: builder.query<Analysis, { id: string; marketIso: string; onlyCompleted?: boolean }>({
      query: ({ id, marketIso, onlyCompleted = false }) => ({
        url: `/games/${id}/analyses/latest`,
        params: {
          onlyCompleted,
          marketIso,
        },
      }),
      providesTags: (result, err, { id }) => [{ type: 'GameAnalysis', id }],
    }),
    getTrending: builder.query<TrendingResponse[], TrendingRequest>({
      query: (body) => ({
        url: '/top-features/trending',
        method: 'POST',
        body,
      }),
    }),
    getGameLatestAndPrevAnalysis: builder.query<{ latest?: Analysis; prev?: Analysis }, { id: string; marketIso: string; onlyCompleted?: boolean }>({
      query: ({ id, marketIso, onlyCompleted = false }) => ({
        url: `/games/${id}/analyses/latestandprev`,
        params: {
          onlyCompleted,
          marketIso,
        },
      }),
      providesTags: (result, err, { id }) => [{ type: 'GameAnalysis', id }],
    }),
    updateFeatureAnswer: builder.mutation<Analysis, { id: string; gameId: string; add: boolean; payload: UpdateAnalysisAnswerPayload }>({
      query: ({ id, add, payload }) => ({
        url: `/analyses/${id}`,
        method: 'PUT',
        params: {
          add,
        },
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: payload,
      }),
      invalidatesTags: (result, err, { gameId }) => [{ type: 'GameAnalysis', id: gameId, state: 'refresh-unfinalized' }],
    }),
    getRefreshAnalysis: builder.query<Analysis, { id: string; gameId: string; unfinalizedOnly?: boolean }>({
      query: ({ id, unfinalizedOnly = true }) => ({
        url: `/analyses/${id}/refresh`,
        params: {
          unfinalizedOnly,
        },
      }),
      providesTags: (result, err, { gameId, unfinalizedOnly }) => [
        { type: 'GameAnalysis', id: gameId, state: unfinalizedOnly ? 'refresh-unfinalized' : 'refresh-finalized' },
      ],
    }),
    finalizeAnalysis: builder.mutation<Analysis, { id: string; gameId: string; type: string; marketIso: string }>({
      query: ({ id, type, marketIso }) => ({
        url: `/analyses/${id}/finalize`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        params: {
          type,
          marketIso,
        },
      }),
      invalidatesTags: (result, err, { gameId }) => [
        { type: 'GameAnalysis', id: gameId, state: 'refresh-finalized' },
        { type: 'GameAnalysis', id: gameId, state: 'refresh-game' },
        { type: 'GameAndAnalysis', id: gameId },
        { type: 'Game', id: gameId },
        { type: 'GamesWithFilter' },
      ],
    }),
    getFeatureDemographicsAvgs: builder.query<{ [marketIso: string]: FeatureDemographic[] }, string[]>({
      async queryFn(args, queryApi, extraOptions, fetchWithBQ) {
        const responses = await Promise.all(args.map(async (marketIso) => fetchWithBQ({ url: `/features/demographics/averages/all?marketIso=${marketIso}` })))
        const errorResponse = responses.find((r) => r.error)
        return errorResponse?.error
          ? { error: errorResponse.error }
          : {
              data: args.reduce(
                (acc, marketIso, i) => ({
                  ...acc,
                  [marketIso]: responses[i].data as FeatureDemographic[],
                }),
                {}
              ),
            }
      },
    }),
    getExplorerDataWithGames: builder.query<MarketExplorerSegmentData, GetExplorerDataWithGamesQueryParams>({
      query: ({ marketIso, conventionalSubgenreIds, dataFilters, ownGames, gameIds }) => ({
        url: '/games/explorer/market',
        method: 'POST',
        params: { ownGames, enableCache: true },
        body: {
          marketIso,
          conventionalSubgenreIds:
            conventionalSubgenreIds &&
            Object.entries(conventionalSubgenreIds)
              .filter(([subgenreId, active]) => active)
              .map(([subgenreId, active]) => subgenreId)
              .join(','),
          dataFilters,
          gameIds: gameIds?.join(','),
        },
      }),
      transformResponse: (response: MarketExplorerSegmentData, _, { userLanguage }) => transformMarketExplorerSegmentData(response, userLanguage),
    }),
    getExplorerDataWithFeatures: builder.query<MarketExplorerSegmentData, GetExplorerDataWithFeaturesQueryParams>({
      query: ({ marketIso, conventionalSubgenreIds, dataFilters, ownGames, choiceLegacyIds }) => ({
        url: '/games/explorer/choices',
        method: 'POST',
        params: { ownGames, enableCache: false },
        body: {
          marketIso,
          conventionalSubgenreIds:
            conventionalSubgenreIds &&
            Object.entries(conventionalSubgenreIds)
              .filter(([subgenreId, active]) => active)
              .map(([subgenreId, active]) => subgenreId)
              .join(','),
          dataFilters,
          choiceLegacyIds: choiceLegacyIds?.join(','),
        },
      }),
      transformResponse: (response: MarketExplorerSegmentData, _, { userLanguage }) => transformMarketExplorerSegmentData(response, userLanguage),
    }),
    getExplorerDataByChoices: builder.query<ExplorerDataByChoice, GetExplorerDataByChoicesQueryParams>({
      query: ({ conventionalSubgenreIds, choiceLegacyIds, ...rest }) => ({
        url: '/games/explorer/choices',
        method: 'POST',
        body: {
          ...rest,
          choiceLegacyIds: choiceLegacyIds?.join(','),
          conventionalSubgenreIds: conventionalSubgenreIds.join(','),
        },
      }),
      transformResponse: (response: ExplorerDataByChoice, _, { userLanguage }) => transformExplorerDataByChoice(response, userLanguage),
    }),

    getExplorerGamesWithChoice: builder.query<string[], GetGamesWithChoiceQueryParams>({
      query: ({ marketIso, gameIds, choiceLegacyId }) => ({
        url: `/games/explorer/gamemap`,
        method: 'POST',
        body: {
          marketIso,
          gameIds,
          choiceLegacyId,
        },
      }),
    }),
    getLatestMotivationProfile: builder.query<ProfileMotivation, MotivationProfileQueryParam>({
      query: ({ id, isOwnGame }) => (isOwnGame ? `/motivations/profiles/private/${id}` : `/motivations/profiles/public/latest/${id}`),
    }),
    getMotivationProfileHistory: builder.query<ProfileMotivation[], MotivationProfileHistoryQueryParam>({
      query: ({ appId }) => `/motivations/profiles/public/history/${appId}`,
    }),
    getMotivationProfileVersions: builder.query<string[], MotivationProfileHistoryQueryParam>({
      query: ({ appId }) => `/motivations/profiles/public/history/${appId}/versions`,
      transformResponse: (response: string[]) => response.filter((value) => value),
    }),
    getMotivationProfileVersion: builder.query<ProfileMotivation, MotivationProfileHistoryVersionQueryParam>({
      query: ({ appId, version }) => `/motivations/profiles/public/history/${appId}/version/${version}`,
    }),
    getMultipleLatestMotivationProfile: builder.query<ProfileMotivation[], { queryParams: MotivationProfileQueryParam[] }>({
      async queryFn(args, queryApi, extraOptions, fetchWithBQ) {
        const responses = await Promise.all(
          args.queryParams.map(async ({ id, isOwnGame }) => fetchWithBQ({ url: `/motivations/profiles/${isOwnGame ? 'private' : 'public/latest'}/${id}` }))
        )
        const data = responses.map((res) => res.data as ProfileMotivation)
        const errorResponse = responses.find((r) => r.error)

        return errorResponse?.error ? { error: errorResponse.error } : { data }
      },
    }),
    getMotivationStats: builder.query<MotivationStatsWithGameScore, MotivationStatsQueryParam>({
      query: ({ appId, conventionalSubgenreId }) => `/motivations/profiles/stats/${appId}/${conventionalSubgenreId}?enableCache=true`,
    }),
    getMultipleMotivationStats: builder.query<(MotivationStatsWithGameScore | null)[], { queryParams: MotivationStatsQueryParam[] }>({
      async queryFn(args, queryApi, extraOptions, fetchWithBQ) {
        const responses = await Promise.all(
          args.queryParams.map(async ({ appId, conventionalSubgenreId, gameId }) => {
            // Skip stats call if no conventionalSubgenreId
            if (!conventionalSubgenreId) {
              return { data: null }
            }

            // Private games (appId = 0) use gameId, public games use appId
            const isPrivateGame = !appId || appId === 0
            const url = isPrivateGame
              ? `/motivations/profiles/private/stats/${gameId}/${conventionalSubgenreId}?enableCache=true`
              : `/motivations/profiles/stats/${appId}/${conventionalSubgenreId}?enableCache=true`
            return fetchWithBQ({ url })
          })
        )
        const data = responses.map((res) => (res.data as MotivationStatsWithGameScore) || null)
        const errorResponse = responses.find((r) => r.error)

        return errorResponse?.error ? { error: errorResponse.error } : { data }
      },
    }),
    getPrivateGameMotivationStats: builder.query<MotivationStatsWithGameScore, { gameId: string; conventionalSubgenreId: string }>({
      query: ({ gameId, conventionalSubgenreId }) => `/motivations/profiles/private/stats/${gameId}/${conventionalSubgenreId}?enableCache=true`,
    }),

    getPublisherGameAppIds: builder.query<number[], { appId: number }>({
      query: ({ appId }) => `artist/byapp/${appId}`,
    }),
    getAnalysisNotes: builder.query<AnalysisNotes, { gameId: string }>({
      query: ({ gameId }) => `analyses/notes/${gameId}`,
      providesTags: (result, err, { gameId }) => [{ type: 'AnalysisNote', gameId }],
    }),
    updateAnalysisNotes: builder.mutation<void, AnalysisNote>({
      query: (body) => ({
        url: '/analyses/notes',
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, err, { gameId }) => [{ type: 'AnalysisNote', gameId }],
    }),
    deleteAnalysisNote: builder.mutation<void, { gameId: string; noteId: string }>({
      query: ({ noteId }) => ({
        url: `/analyses/notes/${noteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, err, { gameId }) => [{ type: 'AnalysisNote', gameId }],
    }),
    translateText: builder.mutation<{ text: string; sourceLanguage: string; targetLanguage: string; status: string }, { text: string; targetLanguage: string }>(
      {
        query: ({ text, targetLanguage }) => ({
          url: `translate`,
          method: 'POST',
          body: {
            text,
            targetLanguage,
          },
        }),
      }
    ),
    getTrackedGame: builder.query<TrackedGame, { gameId: string; userLanguage: UserLanguage }>({
      query: ({ gameId }) => ({
        url: `/games/tracked/${gameId}`,
        method: 'GET',
      }),
      transformResponse: (response: TrackedGame, _, { userLanguage }) => {
        const game = new Game(response.game, { userLanguage })
        return { ...response, game }
      },
    }),
    getTrackedGames: builder.query<TrackedGame[], { eventTypes?: string[]; userLanguage: UserLanguage }>({
      query: ({ eventTypes }) => ({
        url: '/games/tracked/list',
        method: 'POST',
        body: {
          eventTypes,
        },
      }),
      transformResponse: (response: TrackedGamesListResponse, _, { userLanguage }) => {
        const trackedGames: TrackedGame[] = []
        Object.keys(response as TrackedGamesListResponse).forEach((key) => {
          if (key === 'gamesWithPermission' || key === 'gamesWithoutPermission') {
            const permission = key === 'gamesWithPermission' ? true : false
            ;(response as TrackedGamesListResponse)[key as 'gamesWithPermission' | 'gamesWithoutPermission'].forEach((trackedGame) => {
              const game = new Game(trackedGame.game, { userLanguage })
              trackedGames.push({
                ...{ ...trackedGame, game },
                permission,
                game,
              })
            })
          }
        })
        return trackedGames
      },
    }),
    getEventsForTrackedGames: builder.query<TrackingEventsByGame, { gameIds: string[]; startTimestamp: number; endTimestamp: number; marketIso: string }>({
      query: ({ gameIds, startTimestamp, endTimestamp, marketIso }) => ({
        url: '/games/tracked/search',
        method: 'POST',
        body: {
          gameIds,
          startTimestamp,
          endTimestamp,
          marketIso,
        },
      }),
      transformResponse: (response: TrackingEventsByGame) => {
        const transformedEvents = Object.entries(response.events || {}).reduce((acc, [gameId, events]) => {
          const eventsWithExtraData = events.map((event) => {
            return {
              ...event,
              gameId,
              screenshotUrl:
                response.extraEventData && response.extraEventData[event.eventId] ? response.extraEventData[event.eventId].screenshotUrl : undefined,
              comment: response.extraEventData && response.extraEventData[event.eventId] ? response.extraEventData[event.eventId].comment : undefined,
              motivations: response.extraEventData && response.extraEventData[event.eventId] ? response.extraEventData[event.eventId].motivations : undefined,
              mechanicChange: event.blueprintEvolutionData?.types?.includes(LiveEventEvolutionTypeEnum.MechanicChange) || false,
              recurring: response.extraEventData && response.extraEventData[event.eventId] ? response.extraEventData[event.eventId].recurringEvent : false,
            }
          })

          acc[gameId] = eventsWithExtraData

          return acc
        }, {} as TrackingEventByGame)
        response.events = transformedEvents

        const transformedComments = Object.entries(response.comments || {}).reduce((acc, [gameId, comments]) => {
          const commentsWithAnalystData = comments.map((comment) => {
            return {
              ...comment,
              gameId,
              comment: {
                ...comment.comment,
                analyst: Object.values(response.analysts || {}).find((analyst) => analyst.id === comment.comment.analystId),
              },
            }
          })

          acc[gameId] = commentsWithAnalystData

          return acc
        }, {} as { [gameId: string]: LiveEventReview[] })
        response.comments = transformedComments

        const transformedStats = Object.entries(response.stats || {}).reduce((acc, [gameId, stats]) => {
          acc[gameId] = {
            trackingStarted: startOfDay(stats.trackingStarted).getTime(),
            trackingEnded: endOfDay(stats.trackingEnded).getTime(),
          }
          return acc
        }, {} as { [gameId: string]: { trackingStarted: number; trackingEnded: number } })
        response.stats = transformedStats

        return response
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const result = dispatch(
          coreApi.util.updateQueryData('getEventsForTrackedGames', args, (draft) => {
            args.gameIds.reduce(
              (acc, gameId) => {
                acc.events[gameId] = draft?.events?.[gameId] || {}
                if (acc.performanceChanges) {
                  acc.performanceChanges[gameId] = draft?.performanceChanges?.[gameId] || []
                }

                return acc
              },
              { events: {}, performanceChanges: {} } as TrackingEventsByGame
            )
          })
        )
        try {
          await queryFulfilled
        } catch {
          result.undo()
        }
      },
    }),
    getEventsForTrackedGamesSimple: builder.query<{ events: { [gameId: string]: { id: string; name: string }[] } }, { gameIds: string[] }>({
      query: ({ gameIds }) => ({
        url: '/games/tracked/search/simple',
        method: 'POST',
        body: { gameIds },
      }),
    }),
    getFiltersForTrackedGames: builder.query<FiltersForTrackedGamesResponse, { gameIds: string[] }>({
      query: ({ gameIds }) => ({
        url: '/games/tracked/search/filters',
        method: 'POST',
        body: { gameIds },
      }),
    }),
    getTrackedEvent: builder.query<LiveTrackingEvent, string>({
      query: (eventId) => `/games/tracked/events/${eventId}`,
    }),
    // Daily revenue, downloads and DAU of one tracked mobile game over a window of UTC days (both ends included).
    // The KPI chips of the Adopters event tooltip read it, since the event getter carries no performance data and
    // the search must not be used for it. Errors (400: not a mobile game, not tracked, locked) are left to the caller.
    getTrackedGamePerformance: builder.query<TrackedGamePerformance, TrackedGamePerformanceRequest>({
      query: ({ gameId, startTimestamp, endTimestamp, marketIso }) => ({
        url: '/games/tracked/performance',
        method: 'POST',
        body: { gameId, startTimestamp, endTimestamp, marketIso },
      }),
      transformResponse: (response: TrackedGamePerformanceResponse | null): TrackedGamePerformance => ({
        performanceChanges: response?.performanceChanges ?? [],
        performanceValues: response?.performanceValues ?? [],
      }),
      keepUnusedDataFor: TRACKED_GAME_PERFORMANCE_KEEP_UNUSED_DATA_FOR_SECONDS,
    }),
    getTrackedGameLiveEventBlueprints: builder.query<LiveEventCollection, { blueprintId: string; type?: 'all' | 'last' | 'first' }>({
      query: ({ blueprintId, type = 'first' }) => {
        const params = new URLSearchParams()
        params.append('type', type)
        return `/games/tracked/blueprints/${blueprintId}?${params.toString()}`
      },
      transformResponse: (response: LiveEventCollectionDB) => {
        const evolution = response.evolution.reduce((acc, entry: LiveEventEvolutionEntryDB) => {
          const entries = [] as LiveEventEvolutionEntry[]

          entry.start.forEach((start: LiveEventEvolutionEntryStart) => {
            const { start: _, ...entryWithoutStart } = entry
            entries.push({
              ...entryWithoutStart,
              start: start.ts,
              firstInstance: start.firstInstance,
              highlighted: entry.highlighted,
              mechanicChange: entry.types?.includes(LiveEventEvolutionTypeEnum.MechanicChange) || false,
            } as LiveEventEvolutionEntry)
          })

          return [...acc, ...entries]
        }, [] as LiveEventEvolutionEntry[])

        return {
          id: response.id,
          gameId: response.gameId,
          names: response.names,
          evolution: evolution,
        } as LiveEventCollection
      },
    }),
    tutorialVideo: builder.query<void, { tab: string }>({
      query: ({ tab }) => ({
        url: 'users/urlhistory',
        method: 'POST',
        body: { params: { tab }, path: 'main.data-glossary' },
      }),
    }),
    getConventionalCategories: builder.query<ConventionalCategory[], void>({
      query: () => ({
        url: '/conventional/map',
      }),
      transformResponse: (response: { data: ConventionalCategory }) => {
        return Object.entries(response).map(([categoryId, category]) => {
          return {
            id: categoryId,
            name: languageService.getTranslation('conventionalCategories', categoryId),
            priority: 0,
            genres: Object.entries(category).map(([genreId, genre]) => {
              return {
                id: genreId,
                categoryId: categoryId,
                name: languageService.getTranslation('conventionalGenres', genreId),
                subgenres: genre.map((subgenreId: string) => {
                  return {
                    id: subgenreId,
                    name: languageService.getTranslation('conventionalSubgenres', subgenreId),
                    categoryId,
                    genreId,
                  } as ConventionalSubgenre
                }) as ConventionalSubgenre[],
              } as ConventionalGenre
            }) as ConventionalGenre[],
          } as ConventionalCategory
        }) as ConventionalCategory[]
      },
    }),
    getBookmarks: builder.query<BookmarksData, GetBookmarksQueryParams>({
      query: (queryParams: GetBookmarksQueryParams) => ({
        url: `/bookmark/list?isPublic=${queryParams.isPublic}`,
        method: 'GET',
      }),
      transformResponse: (response: Bookmark[], _, args) => {
        const email = args.userEmail

        // Sortb bookmarks by creation date from newest to oldest
        response = response.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))

        // Separate bookmarks by owner
        const bookmarksData = {
          own: response.filter((bookmark) => bookmark.email === email),
          shared: response.filter((bookmark) => bookmark.isPublic && bookmark.email !== email),
        } as BookmarksData

        return bookmarksData
      },
      providesTags: ['Bookmarks'],
    }),
    updateBookmark: builder.mutation<void, Pick<Bookmark, 'id' | 'name' | 'description' | 'imageUrl' | 'urlPath' | 'isPublic'>>({
      query: (bookmark) => ({
        url: `/bookmark/save`,
        method: 'PUT',
        body: bookmark,
      }),
      invalidatesTags: ['Bookmarks'],
    }),
    deleteBookmark: builder.mutation<void, string>({
      query: (bookmarkId) => ({
        url: `/bookmark/${bookmarkId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Bookmarks'],
    }),
    createBookmark: builder.mutation<void, { bookmark: Bookmark; imageDataBase64: string }>({
      query: ({ bookmark, imageDataBase64 }) => ({
        url: `/bookmark/save`,
        method: 'POST',
        body: {
          bookmark,
          screenshot: imageDataBase64,
        },
      }),
      invalidatesTags: ['Bookmarks'],
    }),
    getOwnGamesCount: builder.query<number, void>({
      query: () => ({ url: '/games/count/own', method: 'GET' }),
      providesTags: ['Games'],
    }),
    getAnalystsMap: builder.query<{ [analystId: string]: Analyst }, void>({
      query: () => `analysts/list`,
      transformResponse: (response: Analyst[]) => {
        return response.reduce((acc, analyst) => {
          acc[analyst.id] = analyst
          return acc
        }, {} as { [analystId: string]: Analyst })
      },
    }),
    getConcepts: builder.query<Concept[], void>({
      query: () => `/concepts`,
      providesTags: ['Concepts'],
    }),
    getConcept: builder.query<Concept, string>({
      query: (conceptId) => `/concepts/${conceptId}`,
      providesTags: ['Concepts'],
    }),
    getActiveUsers: builder.query<ActiveUsersData, { appId: number }>({
      query: ({ appId }) => ({
        url: `active_users/app/${appId}`,
        method: 'GET',
      }),
    }),
    // Blueprint Family example images are picked from a tracked event's screenshots,
    // not uploaded, and the BE no longer accepts a `blueprintFamilyGlossaryImage` type here.
    postPublicFiles: builder.mutation<UploadedFile[], { files: File[]; type: 'screenshot'; route: 'public' }>({
      queryFn: async (params, _queryApi, _extraOptions, baseQuery) => {
        const batchSize = 3 // You can configure batch size here
        const resolvedResults: UploadedFile[] = [] // To store the result of all uploads
        const rejectedResults: FetchBaseQueryError[] = [] // To store the error of all uploads
        const { files, type, route } = params
        // Process files in batches
        for (let i = 0; i < files.length; i += batchSize) {
          const batch = files.slice(i, i + batchSize)
          // eslint-disable-next-line no-console
          console.log(`Uploading batch: ${batch.map((file) => file.name).join(', ')}`)

          // Use `baseQuery` for each file upload and collect the results
          const batchResults = await uploadBatch(batch, async (file) => {
            const formData = new FormData()
            formData.set('file', file)
            formData.set('type', type)
            formData.set('route', route)
            return baseQuery({ url: `files/upload/public`, method: 'POST', body: formData })
          })
          resolvedResults.push(...batchResults.map((res) => res.data as UploadedFile)) // Add batch results to the overall results
          rejectedResults.push(...batchResults.filter((r) => r.error).map((r) => r.error as FetchBaseQueryError)) // Add batch errors to the overall errors
        }

        // Return all results, both successful and failed uploads
        return rejectedResults.length > 0 ? { data: undefined, error: rejectedResults[0] } : { data: resolvedResults, error: undefined }
      },
    }),
  }),
})

const transformMarketExplorerSegmentData = (response: MarketExplorerSegmentData, userLanguage: UserLanguage) => ({
  ...response,
  games: response.games.map((game) => new Game(game, { userLanguage })),
  features: response.features.map((feature) => featureService.localizeFeature(feature)) as MarketExplorerSegmentDataFeature[],
})

const transformExplorerDataByChoice = (response: ExplorerDataByChoice, userLanguage: UserLanguage) => ({
  ...response,
  games: response.games.map((game) => new Game(game, { userLanguage })),
  features: response.features && (response.features.map((feature) => featureService.localizeFeature(feature)) as FeatureChoice[]),
})

export const compareNames = (a: { name: string }, b: { name: string }) => (a.name < b.name ? -1 : 1)
const comparePriority = (a: { priority: number }, b: { priority: number }) => a.priority - b.priority

type CommonExplorerDataWithGamesQueryParams = {
  marketIso: string
  conventionalSubgenreIds?: SubgenreMap
  dataFilters?: DataFilters
  ownGames?: boolean
  userLanguage: UserLanguage
}

type GetExplorerDataWithGamesQueryParams = CommonExplorerDataWithGamesQueryParams & {
  gameIds?: string[]
}

type GetExplorerDataWithFeaturesQueryParams = CommonExplorerDataWithGamesQueryParams & {
  choiceLegacyIds?: number[]
}

type GetGamesWithFilterQueryParams = {
  filter: GameFilter[] // "own" returns public and organizations private games, "private" returns only private games, "public" returns only public archived games (internal == true && archive == true), "internal" returns all public games (internal = true) it requires the "internal" user role")
  include: string[]
  marketIso: string
  userLanguage: UserLanguage
}

type GetGameUpdateImpactsQueryParams = {
  marketIso: string
  subgenre?: string
  limit?: number
  ts?: number
  minRevenue?: number
  enableCache?: boolean
}

type GetCompareMultiGamesParams = {
  games: string[]
  marketIso: string[]
  userLanguage: UserLanguage
}

type GetGamesByStageQueryParams = {
  marketIso: string
  stageId: number
  limit?: number
  include: string[]
  includeUnanalyzed: boolean
  userLanguage: UserLanguage
}

type GetGameUpdateImpactsForMarketQueryParams = {
  appId?: string | number
  marketIso: string
}

type GetMultipleGamesUpdateImpactsForMarketQueryParams = {
  apps: GetGameVersionParams[]
}

type GetEstimatesListQueryParams = {
  appIds: number[]
  markets: string[]
  platforms: EstimatePlatformType[]
}

type GetGamesWithChoiceQueryParams = {
  marketIso: string
  gameIds: string[]
  choiceLegacyId: number
}

export type GetBookmarksQueryParams = {
  userEmail: string
  isPublic: boolean
}

type GetExplorerDataByChoicesQueryParams = {
  marketIso: string
  choiceLegacyIds: []
  excludeMotivationData?: boolean
  conventionalSubgenreIds: string[]
  excludeFeatures?: boolean
  userLanguage: UserLanguage
}

type ExplorerDataByChoice = { games: Game[]; features?: FeatureChoice[] }

export type IntercomConversationQueryParams = {
  userMessage: string
  userEmail: string
  assigneeIntercomId: string
  assigneeReplyMessage: string
}

export const unauthenticatedCoreApi = createApi({
  reducerPath: 'unauthenticatedCoreApi',
  baseQuery: fetchBaseQuery({
    baseUrl: window.GR_API_URLS.API_URL_CORE,
  }),
  endpoints: (builder) => ({
    unsubscribeDailyDigest: builder.mutation<void, string>({
      query: (token) => ({
        url: `/public/users/unsubscribe/${token}`,
        method: 'PUT',
      }),
    }),
  }),
})

export const extendedCoreApi = coreApi.injectEndpoints({
  endpoints: (builder) => ({
    getTrackedEventsByGame: builder.query<TrackingEventsByGame[], { gameId: string; startTimestamp: number; endTimestamp: number; marketIso: string }[]>({
      queryFn: async (params, queryApi) => {
        try {
          const gameEvents = await Promise.all(
            params.map(({ gameId, startTimestamp, endTimestamp, marketIso }) => {
              return queryApi
                .dispatch(coreApi.endpoints.getEventsForTrackedGames.initiate({ gameIds: [gameId], startTimestamp, endTimestamp, marketIso }))
                .unwrap()
            })
          )
          return { data: gameEvents }
        } catch (error) {
          return { error: error as FetchBaseQueryError }
        }
      },
    }),
  }),
})

export const { useGetTrackedEventsByGameQuery } = extendedCoreApi

// Export hooks to use in function components
export const {
  useGetGamesQuery,
  useGetGameQuery,
  useGetGameAndAnalysisQuery,
  useGetGameListQuery,
  useGetSettingQuery,
  useGetFeatureQuery,
  useGetFeatureFactsChoiceQuery,
  useGetRevenueAndDownloadAggregatesQuery,
  useGetGamesByStageQuery,
  useSendGameAnalysisSuggestionMutation,
  useGetGameVersionInfoQuery,
  useGetGameVersionInfoListQuery,
  useGetTopFeaturesQuery,
  useGetFeatureByConventionalSubgenreQuery,
  useSearchGamesMutation,
  useSearchArtistQuery,
  useGetGameUpdateImpactsQuery,
  useGetAnalysisCommentQuery,
  useGetGamesWithFilterQuery,
  useGetUpdateImpactsForGameInMarketQuery,
  useGetEstimatesListQuery,
  useGetMarketsQuery,
  useGetBlueprintFamiliesQuery,
  useGetBlueprintFamilyGlossaryQuery,
  useGetBlueprintFamilyQuery,
  useGetBlueprintsQuery,
  useGetBlueprintQuery,
  useGetBlueprintFamilyTrendsQuery,
  useGetStoresQuery,
  useGetGenreTaxonomyQuery,
  useGetConventionalGenreListQuery,
  useGetConventionalSubgenreListQuery,
  useGetLanguageQuery,
  useGetCategoriesMapQuery,
  useGetCategoriesFeaturesQuery,
  useGetScreenshotCommentQuery,
  useGetScreenshotsByIdsQuery,
  useGetScreenshotsByFeaturesQuery,
  useGetScreenshotsByGameIdsQuery,
  useGetScreenshotsByConceptIdsQuery,
  useGetScreenshotCountsQuery,
  useGetScreenshotCountsByGameAndCountryQuery,
  useGetImageObjectUrlQuery,
  useGetKeywordListQuery,
  useGetTagListQuery,
  useGetTagGroupsListQuery,
  useGetVisibleCollectionsQuery,
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useDeleteCollectionMutation,
  useGetGameReviewQuery,
  useGetScreenshotListQuery,
  useGetGameVersionInfoForVersionQuery,
  useGetLatestFtueVideosQuery,
  useGetLatestFtueVideosByGameIdsQuery,
  useGetFtueVideosByGameQuery,
  usePostUploadFileMutation,
  usePostCreateNewGameMutation,
  useGetGameCompetitorsQuery,
  useGetExplorerFeaturesForConventionalSubgenresQuery,
  useGetAnalysisFeatureChoicesQuery,
  useUpdateGameMutation,
  useDeleteGameMutation,
  useCloneGameMutation,
  useGetGameLatestAnalysisQuery,
  useGetGameLatestAndPrevAnalysisQuery,
  useUpdateFeatureAnswerMutation,
  useGetRefreshAnalysisQuery,
  useGetMarketFeaturesQuery,
  useGetFeatureDemographicsAvgsQuery,
  useFinalizeAnalysisMutation,
  useGetUpdateImpactForGameVersionQuery,
  useGetMultipleGamesAndAnalysisQuery,
  useGetTrendingQuery,
  useGetExplorerDataWithGamesQuery,
  useGetExplorerDataWithFeaturesQuery,
  useGetFeatureEffectsQuery,
  useGetLatestMotivationProfileQuery,
  useGetMotivationProfileHistoryQuery,
  useGetMotivationProfileVersionsQuery,
  useGetMotivationProfileVersionQuery,
  useGetMotivationStatsQuery,
  useGetMultipleMotivationStatsQuery,
  useGetPrivateGameMotivationStatsQuery,
  useGetPublisherGameAppIdsQuery,
  useGetAnalysisNotesQuery,
  useUpdateAnalysisNotesMutation,
  useDeleteAnalysisNoteMutation,
  useGetMultipleGameVersionInfoListQuery,
  useGetUpdateImpactsForMultipleGamesInMarketQuery,
  useGetScreenshotCountsByGamesQuery,
  useGetExplorerGamesWithChoiceQuery,
  useGetCompareMultiGamesQuery,
  useGetTrackedGameQuery,
  useGetTrackedGamesQuery,
  useGetEventsForTrackedGamesQuery,
  useGetEventsForTrackedGamesSimpleQuery,
  useGetFiltersForTrackedGamesQuery,
  useGetTrackedEventQuery,
  useGetTrackedGamePerformanceQuery,
  useGetTrackedGameLiveEventBlueprintsQuery,
  useGetMultipleLatestMotivationProfileQuery,
  useGetGameInnovatorListQuery,
  useGetSoftLaunchGamesQuery,
  useStartIntercomConversationQuery,
  useTutorialVideoQuery,
  useGetTutorialVideosQuery,
  useGetAllMarketFeaturesQuery,
  useGetConventionalCategoriesQuery,
  useGetBookmarksQuery,
  useUpdateBookmarkMutation,
  useDeleteBookmarkMutation,
  useCreateBookmarkMutation,
  useGetActiveFeatureListQuery,
  useGetOwnGamesCountQuery,
  useGetAnalystsMapQuery,
  useGetConceptsQuery,
  useGetConceptQuery,
  useGetActiveUsersQuery,
  usePostPublicFilesMutation,
  useTranslateTextMutation,
} = coreApi

export const { useUnsubscribeDailyDigestMutation } = unauthenticatedCoreApi

export type { Category, Collection, ConceptTag, Feature, Keyword, Screenshot, SimpleFeature, Tag, TagGroup, TagGroupList }
